import React, { Component } from "react";
import {
  Button,
  Container,
  Row,
  Form,
  Col,
  Tabs,
  Tab,
  Card,
  Badge,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import configData from "../../config.json";

/**
 * WidgetInstanceEditor - Full config editor with sections:
 * General, Data Source, Columns, Actions, Table Settings, Form Fields
 *
 * Props:
 *   - instanceId: widget_instances_Id to edit (null for new)
 *   - widgetTypes: Array of available widget_types
 *   - onSave: Callback after successful save
 *   - onClose: Callback to close editor
 */
export default class WidgetInstanceEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      activeTab: "general",
      sId: sessionStorage.getItem("sessionId"),
      uId: sessionStorage.getItem("uId"),
      // General
      instanceName: "",
      widget_types_Id: "",
      sourceTable: "",
      isActive: 1,
      // Data Source
      apiEndPoint: "",
      apiMethod: "POST",
      apiParams: "",
      // Configs (as JSON strings for editing)
      columnsConfig: "[]",
      actionsConfig: "{}",
      tableConfig: "{}",
      formConfig: "{}",
      chartConfig: "{}",
      filterConfig: "{}",
      customProps: "{}",
      // JSON validation errors
      jsonErrors: {},
    };
  }

  componentDidMount() {
    if (this.props.instanceId) {
      this.fetchInstance();
    } else {
      this.setState({ loading: false });
    }
  }

  fetchInstance = () => {
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);
    formData.append("widget_instances_Id", this.props.instanceId);

    fetch(configData.api_url + "C_Widget_framework/get_widget_instance", {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success" && data.detail) {
          const d = data.detail;
          this.setState({
            loading: false,
            instanceName: d.instanceName || "",
            widget_types_Id: d.widget_types_Id || "",
            sourceTable: d.sourceTable || "",
            isActive: d.isActive || 1,
            apiEndPoint: d.apiEndPoint || "",
            apiMethod: d.apiMethod || "POST",
            apiParams: d.apiParams || "",
            columnsConfig: d.columnsConfig || "[]",
            actionsConfig: d.actionsConfig || "{}",
            tableConfig: d.tableConfig || "{}",
            formConfig: d.formConfig || "{}",
            chartConfig: d.chartConfig || "{}",
            filterConfig: d.filterConfig || "{}",
            customProps: d.customProps || "{}",
          });
        } else {
          this.setState({ loading: false });
          toast.error("Failed to load widget instance");
        }
      })
      .catch(() => {
        this.setState({ loading: false });
        toast.error("Network error");
      });
  };

  validateJson = (fieldName, value) => {
    const jsonErrors = { ...this.state.jsonErrors };
    try {
      if (value && value.trim()) JSON.parse(value);
      delete jsonErrors[fieldName];
    } catch (e) {
      jsonErrors[fieldName] = "Invalid JSON: " + e.message;
    }
    this.setState({ jsonErrors });
  };

  handleChange = (field, value) => {
    this.setState({ [field]: value });
    if (
      [
        "columnsConfig",
        "actionsConfig",
        "tableConfig",
        "formConfig",
        "chartConfig",
        "filterConfig",
        "customProps",
        "apiParams",
      ].includes(field)
    ) {
      this.validateJson(field, value);
    }
  };

  handleSave = () => {
    if (this.state.isSubmitting) return;
    this.setState({ isSubmitting: true });
    const { jsonErrors } = this.state;
    if (Object.keys(jsonErrors).length > 0) {
      toast.error("Please fix JSON errors before saving");
      this.setState({ isSubmitting: false });
      return;
    }

    this.setState({ saving: true });
    const formData = new FormData();
    formData.append("sId", this.state.sId);
    formData.append("uId", this.state.uId);

    const fields = [
      "instanceName", "widget_types_Id", "sourceTable", "isActive",
      "apiEndPoint", "apiMethod", "apiParams",
      "columnsConfig", "actionsConfig", "tableConfig",
      "formConfig", "chartConfig", "filterConfig", "customProps",
    ];

    fields.forEach((f) => formData.append(f, this.state[f]));

    const isNew = !this.props.instanceId;
    if (!isNew) {
      formData.append("widget_instances_Id", this.props.instanceId);
    }

    const endpoint = isNew
      ? "C_Widget_instances/add"
      : "C_Widget_instances/update";

    fetch(configData.api_url + endpoint, {
      method: "POST",
      mode: "cors",
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        toast[data.status === "success" ? "success" : "error"](data.message);
        this.setState({ saving: false });
        if (data.status === "success" && this.props.onSave) {
          this.props.onSave();
        }
      })
      .catch(() => {
        toast.error("Failed to save");
        this.setState({ saving: false });
      }).finally(() => this.setState({ isSubmitting: false }));
  };

  renderJsonField = (label, field, rows = 6) => {
    const hasError = this.state.jsonErrors[field];
    return (
      <Form.Group className="mb-3">
        <Form.Label>
          {label}
          {hasError && (
            <Badge bg="danger" className="ms-2" style={{ fontSize: "0.7rem" }}>
              Invalid JSON
            </Badge>
          )}
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={rows}
          value={this.state[field]}
          onChange={(e) => this.handleChange(field, e.target.value)}
          isInvalid={!!hasError}
          style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
        />
        {hasError && (
          <Form.Control.Feedback type="invalid">
            {this.state.jsonErrors[field]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
    );
  };

  formatJson = (field) => {
    try {
      const parsed = JSON.parse(this.state[field]);
      this.setState({ [field]: JSON.stringify(parsed, null, 2) });
      const jsonErrors = { ...this.state.jsonErrors };
      delete jsonErrors[field];
      this.setState({ jsonErrors });
    } catch (e) {
      // already marked as invalid
    }
  };

  render() {
    if (this.state.loading) {
      return <div className="text-center p-4">Loading...</div>;
    }

    const widgetTypes = this.props.widgetTypes || [];

    return (
      <Container>
        <Tabs
          activeKey={this.state.activeTab}
          onSelect={(k) => this.setState({ activeTab: k })}
          className="mb-3"
        >
          {/* General Tab */}
          <Tab eventKey="general" title="General">
            <Form.Group className="mb-3">
              <Form.Label>Instance Name</Form.Label>
              <Form.Control
                type="text"
                value={this.state.instanceName}
                onChange={(e) => this.handleChange("instanceName", e.target.value)}
                placeholder="e.g., testtable_table"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Widget Type</Form.Label>
              <Form.Select
                value={this.state.widget_types_Id}
                onChange={(e) => this.handleChange("widget_types_Id", e.target.value)}
              >
                <option value="">Select Widget Type</option>
                {widgetTypes.map((wt) => (
                  <option key={wt.widget_types_Id} value={wt.widget_types_Id}>
                    {wt.typeName} ({wt.typeKey})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Source Table</Form.Label>
              <Form.Control
                type="text"
                value={this.state.sourceTable}
                onChange={(e) => this.handleChange("sourceTable", e.target.value)}
                placeholder="e.g., testtable"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                label="Is Active"
                checked={parseInt(this.state.isActive) === 1}
                onChange={(e) => this.handleChange("isActive", e.target.checked ? 1 : 0)}
              />
            </Form.Group>
          </Tab>

          {/* Data Source Tab */}
          <Tab eventKey="datasource" title="Data Source">
            <Form.Group className="mb-3">
              <Form.Label>API Endpoint</Form.Label>
              <Form.Control
                type="text"
                value={this.state.apiEndPoint}
                onChange={(e) => this.handleChange("apiEndPoint", e.target.value)}
                placeholder="e.g., C_Testtable/list"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>API Method</Form.Label>
              <Form.Select
                value={this.state.apiMethod}
                onChange={(e) => this.handleChange("apiMethod", e.target.value)}
              >
                <option value="POST">POST</option>
                <option value="GET">GET</option>
              </Form.Select>
            </Form.Group>
            {this.renderJsonField("API Params (JSON)", "apiParams", 3)}
          </Tab>

          {/* Columns Tab */}
          <Tab eventKey="columns" title="Columns">
            <div className="d-flex justify-content-end mb-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => this.formatJson("columnsConfig")}
              >
                Format JSON
              </Button>
            </div>
            {this.renderJsonField("Columns Config (JSON Array)", "columnsConfig", 12)}
            <Card className="bg-light">
              <Card.Body style={{ fontSize: "0.8rem" }}>
                <strong>Schema:</strong> {"[{ field, header, sortable, type, width }]"}
                <br />
                <strong>Types:</strong> text, number, date, boolean
              </Card.Body>
            </Card>
          </Tab>

          {/* Actions Tab */}
          <Tab eventKey="actions" title="Actions">
            <div className="d-flex justify-content-end mb-2">
              <Button variant="outline-secondary" size="sm" onClick={() => this.formatJson("actionsConfig")}>
                Format JSON
              </Button>
            </div>
            {this.renderJsonField("Actions Config (JSON)", "actionsConfig", 8)}
            <Card className="bg-light">
              <Card.Body style={{ fontSize: "0.8rem" }}>
                <strong>Schema:</strong>{" "}
                {"{ canAdd, canEdit, canDelete, addEndpoint, editEndpoint, deleteEndpoint, permissionPrefix }"}
              </Card.Body>
            </Card>
          </Tab>

          {/* Table Settings Tab */}
          <Tab eventKey="table" title="Table">
            <div className="d-flex justify-content-end mb-2">
              <Button variant="outline-secondary" size="sm" onClick={() => this.formatJson("tableConfig")}>
                Format JSON
              </Button>
            </div>
            {this.renderJsonField("Table Config (JSON)", "tableConfig", 6)}
          </Tab>

          {/* Form Fields Tab */}
          <Tab eventKey="form" title="Form">
            <div className="d-flex justify-content-end mb-2">
              <Button variant="outline-secondary" size="sm" onClick={() => this.formatJson("formConfig")}>
                Format JSON
              </Button>
            </div>
            {this.renderJsonField("Form Config (JSON)", "formConfig", 12)}
            <Card className="bg-light">
              <Card.Body style={{ fontSize: "0.8rem" }}>
                <strong>Schema:</strong>{" "}
                {'{ fields: [{ fieldName, displayName, inputType, required, options }] }'}
              </Card.Body>
            </Card>
          </Tab>

          {/* Chart Tab */}
          <Tab eventKey="chart" title="Chart">
            <div className="d-flex justify-content-end mb-2">
              <Button variant="outline-secondary" size="sm" onClick={() => this.formatJson("chartConfig")}>
                Format JSON
              </Button>
            </div>
            {this.renderJsonField("Chart Config (JSON)", "chartConfig", 10)}
          </Tab>

          {/* Custom Tab */}
          <Tab eventKey="custom" title="Custom">
            {this.renderJsonField("Filter Config (JSON)", "filterConfig", 6)}
            {this.renderJsonField("Custom Props (JSON)", "customProps", 6)}
          </Tab>
        </Tabs>

        <Row className="mt-3">
          <Col className="d-flex justify-content-end gap-2">
            {this.props.onClose && (
              <Button variant="outline-secondary" onClick={this.props.onClose}>
                Cancel
              </Button>
            )}
            <Button
              variant="success"
              onClick={this.handleSave}
              disabled={this.state.saving || Object.keys(this.state.jsonErrors).length > 0}
            >
              {this.state.saving ? "Saving..." : this.props.instanceId ? "Update" : "Create"}
            </Button>
          </Col>
        </Row>
      </Container>
    );
  }
}
